import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { PAYMENT_MODES } from '../lib/constants';
import { Plus, Trash2, X, IndianRupee, ChevronDown, Search, Receipt } from 'lucide-react';

export default function PaymentsPage() {
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState('');
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');

  const [form, setForm] = useState({
    subscriberId: '', amount: 0, date: new Date().toISOString().split('T')[0], mode: 'UPI / GPay', remarks: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, paymentsRes] = await Promise.all([
        api.getSubscribers({}),
        api.getPayments(selectedSub ? { subscriberId: selectedSub } : {})
      ]);
      setSubs(subsRes.data);
      setPayments(paymentsRes.data);
      if (selectedSub) {
        try {
          const sumRes = await api.getPaymentSummary(selectedSub);
          setSummary(sumRes.data);
        } catch { setSummary(null); }
      } else {
        setSummary(null);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [selectedSub]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getSubName = (id) => {
    const s = subs.find(s => s.id === id);
    return s ? `${s.name} (${s.apt}/${s.flat})` : id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createPayment(form);
      setShowModal(false);
      setForm({ subscriberId: selectedSub || '', amount: 0, date: new Date().toISOString().split('T')[0], mode: 'UPI / GPay', remarks: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    await api.deletePayment(id);
    fetchData();
  };

  const filteredPayments = search
    ? payments.filter(p => {
        const subName = getSubName(p.subscriberId).toLowerCase();
        return subName.includes(search.toLowerCase());
      })
    : payments;

  return (
    <div data-testid="payments-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Payments</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Track payment history per subscriber</p>
        </div>
        <button data-testid="add-payment-button" onClick={() => { setForm({ ...form, subscriberId: selectedSub || '' }); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Record Payment
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#78716C' }} />
          <input data-testid="payment-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by subscriber name..." className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }} />
        </div>
        <div className="relative">
          <select data-testid="payment-filter-sub" value={selectedSub} onChange={e => { setSelectedSub(e.target.value); setLoading(true); }} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8 min-w-[200px]" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            <option value="">All Subscribers</option>
            {subs.map(s => <option key={s.id} value={s.id}>{s.name} ({s.apt})</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
      </div>

      {/* Payment Summary for selected subscriber */}
      {summary && selectedSub && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl p-5" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
            <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#A8A29E' }}>Total Payable</div>
            <div className="flex items-center gap-1">
              <IndianRupee className="w-4 h-4" style={{ color: '#C8A96E' }} />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{summary.payableAmount?.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div className="rounded-xl p-5" style={{ background: '#1E1A17', border: '1px solid rgba(74,222,128,0.2)' }}>
            <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#A8A29E' }}>Total Paid</div>
            <div className="flex items-center gap-1">
              <IndianRupee className="w-4 h-4" style={{ color: '#4ADE80' }} />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#4ADE80' }}>{summary.totalPaid?.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div className="rounded-xl p-5" style={{ background: '#1E1A17', border: `1px solid ${summary.balance > 0 ? 'rgba(248,113,113,0.2)' : 'rgba(74,222,128,0.2)'}` }}>
            <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#A8A29E' }}>Balance Due</div>
            <div className="flex items-center gap-1">
              <IndianRupee className="w-4 h-4" style={{ color: summary.balance > 0 ? '#F87171' : '#4ADE80' }} />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: summary.balance > 0 ? '#F87171' : '#4ADE80' }}>{Math.abs(summary.balance)?.toLocaleString('en-IN')}</span>
              {summary.balance <= 0 && <span className="text-xs ml-1" style={{ color: '#4ADE80' }}>Paid</span>}
            </div>
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
        <div className="overflow-x-auto">
          <table className="w-full gm-table">
            <thead><tr><th>Subscriber</th><th>Date</th><th>Amount</th><th>Mode</th><th>Remarks</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: '#78716C' }}>Loading...</td></tr>
              ) : filteredPayments.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: '#78716C' }}>
                  <Receipt className="w-8 h-8 mx-auto mb-2" style={{ color: '#2A241F' }} />
                  No payment records found
                </td></tr>
              ) : filteredPayments.map((p, i) => (
                <tr key={p.id} className="fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <td style={{ color: '#F5F5F4' }} className="font-medium">{getSubName(p.subscriberId)}</td>
                  <td>{p.date}</td>
                  <td>
                    <span className="inline-flex items-center gap-1" style={{ color: '#4ADE80' }}>
                      <IndianRupee className="w-3 h-3" />{p.amount?.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(200,169,110,0.1)', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>{p.mode}</span></td>
                  <td className="text-xs" style={{ color: '#A8A29E' }}>{p.remarks}</td>
                  <td>
                    <button data-testid={`delete-payment-${p.id}`} onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Record Payment</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Subscriber *</label>
                <div className="relative">
                  <select data-testid="payment-subscriber" required value={form.subscriberId} onChange={e => setForm({...form, subscriberId: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    <option value="">-- select --</option>
                    {subs.map(s => <option key={s.id} value={s.id}>{s.name} ({s.apt}/{s.flat})</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Amount (Rs.) *</label>
                  <input data-testid="payment-amount" type="number" required min={1} value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Date *</label>
                  <input data-testid="payment-date" type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Payment Mode *</label>
                <div className="relative">
                  <select data-testid="payment-mode" required value={form.mode} onChange={e => setForm({...form, mode: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Remarks</label>
                <input data-testid="payment-remarks" value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} placeholder="e.g. March 2026 subscription" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="save-payment-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>Save Payment</button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
